"use server";

import { getRequestEvent } from "solid-js/web";
import {
  BrandExtraMetadata,
  DiscountDetailsItem,
  SimulatedTransactionDetails,
  VoucherProduct,
  OfferConstruct,
  SimulateTransactionRequest,
} from "../types/brand";
import { Mode } from "~/types";
import { redirect } from "@solidjs/router";
import { getStaticProxyJson } from "../static";
import { serverRepo } from "../apis/server_repo";
import { clientIdConfig } from "~/data/partner_config";
import {
  defaultRequestForADenominationsBrand,
  defaultRequestForAVariantBrand,
} from "~/utils/webstore/default_simulate_request";

type FAQObject = {
  "@type": "Question";
  name: string;
  acceptedAnswer: {
    "@type": "Answer";
    text: string;
  };
};

type SEOSchema = {
  alternateName: string;
  sameAs: string[];
  keywords: string;
  disambiguatingDescription: string;
  faqObjs: FAQObject[];
};

type MetadataSEOSchema = Record<string, SEOSchema>;

export type BrandL2RouteData = {
  brand: VoucherProduct;
  brandExtraMetadata: BrandExtraMetadata;
  simulate: IntialSimulationData;
  hubbleCoinRewards?: DiscountDetailsItem;
  mode: Mode;
  isLoggedIn: boolean;
  clientId: string;
  metadataSEOSchema?: MetadataSEOSchema;
  offerConstruct?: OfferConstruct;
};

export const getBrandL2RouteData = async (
  brandKey: string
): Promise<BrandL2RouteData> => {
  const requestEvent = getRequestEvent();
  const mode = requestEvent?.locals.mode ?? "hubble";
  const isSessionPresent = requestEvent?.locals.sid;

  let offerConstruct$P: Promise<OfferConstruct> | undefined;

  if (getRequestEvent()?.locals.clientId && isSessionPresent) {
    if (
      requestEvent?.locals.clientId &&
      requestEvent?.locals.clientId === clientIdConfig.tdClientId
    ) {
      offerConstruct$P = serverRepo.getOfferConstruct();
    }
  } else if (mode === "rnr" && !isSessionPresent) {
    throw redirect(`/logout`);
  }

  const brand$P = serverRepo.getBrand(brandKey);
  const brandExtraMetadata$P = serverRepo.getBrandExtraMetadata(brandKey);
  const metadataSEOSchema$P = getStaticProxyJson(
    "brandl2_metadata_schema.json"
  );

  const resolved = await Promise.all([
    brand$P,
    brandExtraMetadata$P,
    metadataSEOSchema$P,
    offerConstruct$P ?? Promise.resolve(undefined),
  ]);

  const brand = resolved[0];
  const brandExtraMetadata = resolved[1];
  const metadataSEOSchema = resolved[2];
  const offerConstruct = resolved[3];

  const isVariantBrand = brand.variantDetailedResponse != null;
  const isDenominationsBrand = brand.amountConditions.denominations != null;
  let initialSimulateTransactionDetails;
  let initialSimulateError;
  let hubbleCoinRewards;
  let simulateResponses: Array<{ data: SimulatedTransactionDetails[] }> = [];

  try {
    let simulateRequest: SimulateTransactionRequest = {
      voucherProductId: brand.externalId,
      amount: brand.amountConditions.defaultAmount,
      consumeCoins: mode === "rnr",
    };

    if (isVariantBrand) {
      simulateRequest = {
        ...simulateRequest,
        ...defaultRequestForAVariantBrand(brand),
      };
    } else if (isDenominationsBrand) {
      simulateRequest = {
        ...simulateRequest,
        ...defaultRequestForADenominationsBrand(brand),
      };
    }

    if (
      requestEvent?.locals.clientId &&
      requestEvent?.locals.clientId === clientIdConfig.tdClientId
    ) {
      let leastPossibleAmount = brand.amountConditions.minAmount;
      simulateRequest = {
        ...simulateRequest,
        amount: leastPossibleAmount,
      };
    }

    if (
      requestEvent?.locals.clientId &&
      requestEvent?.locals.clientId === clientIdConfig.tdClientId &&
      offerConstruct?.type === "SUBSCRIPTION_REWARD"
    ) {
      const subscriptionDetails =
        offerConstruct?.subscriptionRewardDetails || [];

      const simulatePromises = subscriptionDetails.map((detail) => {
        return serverRepo.simulate({
          ...simulateRequest,
          subscriptionDetails: { tenure: detail.tenure },
        });
      });

      simulateResponses = await Promise.all(simulatePromises);

      initialSimulateTransactionDetails = simulateResponses.map((response) =>
        response.data.filter((data) => data.paymentRoute != "WALLET")
      );
    } else {
      const simulateResponse = await serverRepo.simulate(simulateRequest);

      initialSimulateTransactionDetails = simulateResponse.data.filter(
        (data) => data.paymentRoute != "WALLET"
      )[0];

      const { totalDiscount, coinDiscount } =
        initialSimulateTransactionDetails?.discountDetails;

      if (brand.rewardType === "POSTPAID") {
        hubbleCoinRewards = {
          amount: totalDiscount.amount - coinDiscount.amount,
          percentage: totalDiscount.percentage - coinDiscount.percentage,
        };
      }
    }
  } catch (error: any) {
    console.log(error.message);
    initialSimulateError = error.message;
  }

  let routeData = {
    brand,
    brandExtraMetadata,
    simulate: {
      initialSimulateTransactionDetails,
      initialSimulateError,
    },
    mode,
    isLoggedIn: getRequestEvent()?.locals.sid !== undefined,
    clientId: requestEvent?.locals?.clientId,
    ...(hubbleCoinRewards && { hubbleCoinRewards }),
    metadataSEOSchema,
    offerConstruct,
  } as BrandL2RouteData;

  return routeData;
};

export interface IntialSimulationData {
  initialSimulateTransactionDetails: SimulatedTransactionDetails[][] | null;
  initialSimulateError: string | null;
}
